<template>
  <div class="meta-info-controls">
    <div>
      <MetaPlayButton
        v-if="showPlayButton"
        :disabled="disablePlayButton"
        :class="PLAY_BUTTON_CLASS"
        :book-type="bookType"
        @play-button-clicked="handlePlayButtonClicked"
      />
    </div>
    <div class="meta-info-controls-bottom" :class="{ '-row': !showPlayButton }">
      <div>
        <slot name="custom-control"></slot>
      </div>
      <div class="save-button-wrapper" :class="{ '-shrunk': !showPlayButton }">
        <Downloader
          v-if="showPlayButton"
          :disabled="disabled"
          :book-id="bookId"
          :post-action-name="'LibraryStore/updatePublicationSource'"
          outlined
          x-large
          block
          @downloaderEvent="downloaderEventHandler"
        />
        <BaseButton
          v-if="isCompilationOpen && !isDevice"
          :id="menuActivatorId"
          :disabled="isGuestUser"
          :aria-label="$t('Accessibility.compilations.menu')"
          color="black"
          icon
          x-large
          outlined
          class="compilation-menu-activator"
          @click.native.prevent="openCompilationMenu"
          ><BaseSpriteIcon :icon-name="'ico-hidden-menu'"></BaseSpriteIcon
        ></BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import MetablockEventEnum from '@/enums/MetablockEventEnum';
import DownloaderEventEnum from '@/enums/DownloaderEventEnum';
import AppModeEnum from '@/enums/AppModeEnum';
import PopupNamesEnum from '@/enums/PopupNamesEnum';

import MetaPlayButton from '@/components/views/Audio/MetaPlayButton';
import BaseButton from '@/components/base/BaseButton/BaseButton.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';

import GoogleAnalyticsUtils from '@/services/utils/GoogleAnalyticsUtils';

const BASE_MENU_ACTIVATOR_ID = 'compilation-menu-activator';

export default {
  name: 'ControlsMetaBlock',
  components: {
    Downloader: () => import('@/components/views/Downloader/Downloader.vue'),
    MetaPlayButton,
    BaseButton,
    BaseSpriteIcon
  },
  props: {
    bookId: {
      type: String,
      required: true
    },
    audio: {
      type: Boolean,
      required: true
    },
    bookName: {
      type: String,
      required: false,
      default: ''
    },
    isContentReady: {
      type: Boolean,
      required: true
    },
    bookType: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const PLAY_BUTTON_CLASS = AppConstantsUtil.PLAY_BUTTON_CLASS;

    return {
      PLAY_BUTTON_CLASS,
      trackingItemView: {},
      currentMode: null,
      menuActivatorId: `${BASE_MENU_ACTIVATOR_ID}-${this._uid}`,
      menuActivatorSelector: `#${BASE_MENU_ACTIVATOR_ID}-${this._uid}`
    };
  },
  computed: {
    ...mapGetters('ContextStore', ['isDevice']),
    showPlayButton() {
      return this.currentMode === AppModeEnum.READER && this.audio;
    },
    isDownloaded() {
      return (
        this.audio &&
        this.trackingItemView.isContentDownloaded &&
        this.trackingItemView.isAudioDownloaded &&
        !this.trackingItemView.outdatedData
      );
    },
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    isGuestUser() {
      return this.$store.getters['UserStore/isGuestUser'];
    },
    isCompilationOpen() {
      return this.$store.getters['PublicationStore/isCompilationOpen'];
    },
    disablePlayButton() {
      return (
        this.disabled ||
        !this.isContentReady ||
        (!this.isDownloaded && !this.isOnline)
      );
    }
  },
  created() {
    this.currentMode = this.$store.getters['ContextStore/appModeGetter'];
  },
  methods: {
    downloaderEventHandler(payload) {
      const { type, data } = payload;
      if (type !== DownloaderEventEnum.INIT) {
        GoogleAnalyticsUtils.trackEvent(
          'book',
          'downloader',
          type,
          this.bookName
        );
      }

      switch (type) {
        case DownloaderEventEnum.DELETED:
        case DownloaderEventEnum.INIT:
        case DownloaderEventEnum.DOWNLOADED:
          this.$_updateTrackingItemView(data);
          break;
        case DownloaderEventEnum.UPDATED:
          this.$_emitEvent(MetablockEventEnum.UPDATED_BOOK, data);
          break;
        default:
          break;
      }
    },
    $_updateTrackingItemView(data) {
      const { trackingItemView } = data;
      this.trackingItemView = trackingItemView;
    },
    $_emitEvent(type, data = {}) {
      this.$emit('controlsMetaBlockEvent', {
        type,
        data
      });
    },
    async openCompilationMenu() {
      if (this.isPopupOpened) {
        await this.$store.dispatch('ManagePopupStore/closePopup', {
          name: PopupNamesEnum.COMPILATION_ITEM_MENU
        });
      }
      const compilation = {
        id: this.bookId,
        audio: this.audio
      };
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.COMPILATION_ITEM_MENU,
        popupContext: {
          activatorSelector: this.menuActivatorSelector,
          compilation: compilation,
          audio: this.audio
        }
      });
    },
    handlePlayButtonClicked() {
      this.$_emitEvent('play-button-clicked');
    }
  }
};
</script>
